import React, { useEffect } from 'react';

const ASI1Extended = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI1-Mini-Extended</h1>
          <p className="text-xl opacity-90">
            Advanced Reasoning for Web3 & Agentic Applications
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              We're excited to announce the release of ASI1-Mini-Extended, our first dedicated reasoning model designed with the Web3 community in mind. This model delivers exceptional reasoning capabilities in a compact and efficient package.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Performance Highlights</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <ul className="list-disc pl-6 text-gray-700 space-y-3">
                <li className="font-medium">MMLU Benchmark Score: <span className="text-blue-700 font-bold">89%</span> - Outperforming larger models including Google Gemini 2.0 Flash (86%), GPT-4.0 (86%), and Llama 3.1 405B (85%)</li>
                <li className="font-medium">4% Improvement over our previous ASI1-Mini model (85%)</li>
                <li className="font-medium">Efficient Design - Runs on a single L40 GPU despite competitive performance with much larger models</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Why ASI1‑Mini‑Extended Outperforms Our Previous Model</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Superior multi‑step problem solving</h3>
                <p className="text-gray-700">
                  Handles complex tasks by breaking them down into logical reasoning steps, dramatically improving accuracy. This structured decomposition mirrors expert analysts, enabling precise intermediate validations, early error detection, and seamless integration with on‑chain pipelines for decentralized applications and smart‑contract audits.
                </p>
              </div>

              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Enhanced decision‑making capability</h3>
                <p className="text-gray-700">
                  Utilizes explicit reasoning traces to evaluate multiple solution paths before selecting optimal approaches. These transparent traces foster collaborative debugging, enable policy checks, and empower downstream agents to inherit context, adjust parameters, and finalize blockchain transactions during volatile conditions.
                </p>
              </div>

              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Greater adaptability to ambiguous instructions</h3>
                <p className="text-gray-700">
                  The reasoning architecture enables better interpretation of unclear or incomplete requirements. It builds probabilistic intent graphs and prompts clarifying questions, reducing misalignment so smart contracts or DAOs receive scoped actions despite sparse or outdated developer documentation.
                </p>
              </div>

              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Improved self‑correction</h3>
                <p className="text-gray-700">
                  Can identify and address logical errors mid‑process, reducing cascading mistakes in complex workflows. Sanity checks compare intermediate outputs against domain constraints, triggering rollbacks and retries to safeguard long‑running cross‑chain operations and protect user funds from unintended state transitions.
                </p>
              </div>

              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">More reliable agentic behavior</h3>
                <p className="text-gray-700">
                  Makes consistently better decisions when operating autonomously on multi‑stage tasks. Hierarchical planning allocates subtasks to specialized agents, monitors execution metrics, and reprioritizes steps in real time, maintaining robust autonomy despite fluctuating latency and gas fees.
                </p>
              </div>

              <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Higher performance ceiling</h3>
                <p className="text-gray-700">
                  Shows continued improvement potential as reasoning depth increases, unlike traditional models. Layered cognition enables the system to refine hypotheses between iterations, absorbing new domain data and patterns without architectural overhauls, future‑proofing deployments against protocol evolution.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Trustworthiness & Safety Focus</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Transparent reasoning paths</h3>
                  <p className="text-gray-700">All conclusions include traceable reasoning steps that can be audited. Each inference node is timestamped and hashed, allowing third‑party verifiers to reproduce chains of thought and anchor them immutably on decentralized ledgers for regulatory compliance.</p>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Reduced hallucination risk</h3>
                  <p className="text-gray-700">The reasoning architecture significantly reduces the probability of fabricated outputs. Confidence thresholds gate uncertain conclusions, while cross‑checking with knowledge bases and blockchain state ensures generated answers remain grounded, verifiable, and aligned with up‑to‑date on‑chain facts.</p>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Consistency checking</h3>
                  <p className="text-gray-700">Built‑in verification of reasoning consistency helps prevent logical errors. Constraint solvers evaluate intermediate outputs against formal specifications and policies, flagging discrepancies early and providing diffs so auditors can review and approve necessary corrections promptly.</p>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Security‑first approach</h3>
                  <p className="text-gray-700">Designed to highlight potential security implications when analyzing sensitive operations. Threat‑modeling heuristics automatically surface privilege escalations, re‑entrancy vectors, and oracle manipulations, recommending mitigation steps and gas‑efficient patches tailored to project coding standards before deployment.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Development Approach</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We leveraged our previous ASI1-Mini model to generate synthetic large-scale training data, which has proven effective even without applying reinforcement learning techniques. The model demonstrates impressive abilities in solving complex problems and implementing sophisticated workflows.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Roadmap: Future Enhancements</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              This release represents just the beginning of our new reasoning series. Upcoming iterations will feature:
            </p>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <ul className="list-disc pl-6 text-gray-700 space-y-3">
                <li>Multiple reasoning modes with dynamic reasoning and user control options</li>
                <li>End-to-end agentic reasoning for more autonomous operation</li>
                <li>Multi-type tool and agent structures including specialized read, write, and execution agents with different criticality levels, roles, and permissions for highly sensitive tasks</li>
                <li>Expanded training data - We're using ASI1-Mini-Extended to create new datasets, scaling data 5x for future versions</li>
                <li>Performance optimization through implementation of custom RLHF techniques</li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              We're actively developing the next iteration and expect to release updates soon. Join our growing community of Web3 developers and agentic system builders leveraging ASI1-Mini-Extended for more reliable, transparent, and capable applications.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASI1Extended;
